'use client';

import { useMemo } from 'react';
import { getLanguage, type Language, useLanguageStore } from './language-store';
import { translateText } from './translations';

export function localeForLanguage(language: Language): string {
  return language === 'en' ? 'en-GB' : 'de-DE';
}

export function translate(language: Language, text: string): string {
  return translateText(text, language);
}

export function formatDateByLanguage(
  language: Language,
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) {
    return '-';
  }

  try {
    return new Date(value).toLocaleDateString(localeForLanguage(language), {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(options ?? {}),
    });
  } catch {
    return String(value);
  }
}

export function formatDateTimeByLanguage(
  language: Language,
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) {
    return '-';
  }

  try {
    return new Date(value).toLocaleString(localeForLanguage(language), {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(options ?? {}),
    });
  } catch {
    return String(value);
  }
}

export function useI18n() {
  const language = useLanguageStore((state) => state.language);
  const setLanguage = useLanguageStore((state) => state.setLanguage);

  return useMemo(
    () => ({
      language,
      setLanguage,
      locale: localeForLanguage(language),
      t: (text: string) => translate(language, text),
      formatDate: (value: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions) =>
        formatDateByLanguage(language, value, options),
      formatDateTime: (value: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions) =>
        formatDateTimeByLanguage(language, value, options),
    }),
    [language, setLanguage],
  );
}

export function getCurrentLanguage(): Language {
  return getLanguage();
}
